import React, { useState } from 'react';
import { DarkDownArrow, DarkUpArrow, GreenTick } from 'src/assets/images';
import { LoaderAnimation } from 'src/components';
import { Button, QuantitySpinInput } from 'src/features/common';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import { DEVICE_NAMES } from '../../tech-debt/product-details-extra';
import { TertiaryButton } from 'src/common';
import { Environment } from 'src/Environment';

import './product-card.scss';

export type ProductCardProps = {
  image: string;
  title: string;
  points?: string[];
  showMoreFeatures?: string[];
  icons?: string[];
  price?: string;
  priceSuffix?: string;
  isRetailProduct: boolean;
  onBoughtInstoreClicked?: () => void;
  minimalDisplay?: boolean;
  hasLoaded: boolean;
  quantity?: number;
  badge?: { color?: string; text: string };
  className?: string;
  singleSelection?: boolean;
  smallImage?: boolean;
  smallCard?: boolean;
  [rest: string]: any;
};

export const ProductCard = ({
  image,
  title,
  points,
  showMoreFeatures,
  icons,
  price,
  priceSuffix,
  minimalDisplay,
  hasLoaded,
  badge,
  className,
  singleSelection = false,
  smallImage = false,
  smallCard = false,
  ...rest
}: ProductCardProps) => {
  const isToPComingSoon =
    title === DEVICE_NAMES.TAP_ON_PHONE &&
    Environment.current.disabledFeatures?.tapOnPhoneComingSoon;
  const [showChildren, setShowChildren] = useState(false);

  const removeFieldName = (obj: { [key: string]: any }) => {
    if (obj.field && 'name' in obj.field) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { name, ...objRest } = obj.field;

      obj.field = objRest;
    }

    return obj;
  };

  return (
    <article
      className={`product-card-container ${className || ''} ${
        singleSelection && rest?.field?.value ? 'grey-out' : ''
      } ${smallImage ? 'small-product' : ''}`}
    >
      <img
        className="product-card-image"
        src={rest?.field?.value && smallImage ? GreenTick : image}
        alt={title}
      />

      {badge?.text && <aside className={`badge ${badge.color}`}>{badge.text}</aside>}

      <h5>{title}</h5>
      {points && (
        <ul className="tick-list">
          {points.map((point, index) => (
            <li key={`${title} ${index}`}>{point}</li>
          ))}
        </ul>
      )}

      {icons && (
        <ul className={`image-row ${smallCard && 'small'}`}>
          {icons.map((icon, index) => (
            <li key={`${title} icon ${index}`}>
              <img className={`${smallCard && 'small'}`} src={icon} alt="icon" />
            </li>
          ))}
        </ul>
      )}

      {hasLoaded ? (
        <p>
          {price && (
            <>
              <mark>{price}</mark>
              {priceSuffix ? ` / ${priceSuffix}` : ''}
            </>
          )}
        </p>
      ) : (
        <LoaderAnimation width="10rem" height="10rem" />
      )}

      <hr className={`${smallCard && 'small'}`} />

      {showMoreFeatures && showMoreFeatures?.length !== 0 && !minimalDisplay && (
        <section className="show-more-block">
          <button
            type="button"
            className="show-more-btn"
            onClick={() => setShowChildren(!showChildren)}
          >
            Show {showChildren ? 'less' : 'more'}
            <img src={showChildren ? DarkUpArrow : DarkDownArrow} alt="" />
          </button>
          {showChildren && (
            <section>
              <ul className="tick-list">
                {showMoreFeatures?.map(
                  (point, index) => point && <li key={`${title} ${index}`}>{point}</li>,
                )}
              </ul>
            </section>
          )}
        </section>
      )}

      <footer className={`product-card-footer ${smallCard && 'small'}`}>
        {singleSelection ? (
          <Button
            className={`${rest?.field?.value ? 'green-btn' : ''}`}
            buttonStyle={DefaultButtonStyle().hasTertiaryAction().build()}
            buttonParams={{ type: ButtonParamType.CheckboxButton, name: rest?.field?.name }}
            {...removeFieldName(rest)}
          >
            {rest?.field?.value ? 'Unselect' : 'Select'}
          </Button>
        ) : title === DEVICE_NAMES.VODAPAY_ECOM_GATEWAY ||
          title === DEVICE_NAMES.TAP_ON_PHONE ||
          title === DEVICE_NAMES.VODAPAY_PAYMENT_REQUEST ? (
          <TertiaryButton
            type="button"
            disabled={isToPComingSoon}
            selected={rest?.field?.value}
            onClick={() => rest.form.setFieldValue(rest.field.name, Number(!rest.field.value))}
          >
            {rest?.field?.value
              ? 'Remove from cart'
              : isToPComingSoon
              ? 'Coming soon!'
              : 'Add to cart'}
          </TertiaryButton>
        ) : (
          <QuantitySpinInput
            automationid={rest?.automationId}
            lightMode
            disabled={!hasLoaded}
            min={rest?.min}
            max={rest?.max}
            name={rest?.field?.name}
            {...rest}
          />
        )}
      </footer>
    </article>
  );
};
